import React, { useState } from 'react';
import {
  TriggerFilter,
  FilterFieldDefinition,
  FilterOperator,
  FilterLogic,
  getOperatorLabel,
  operatorRequiresValue,
  operatorRequiresTwoValues,
  operatorRequiresMultipleValues
} from '../../types/triggerFilters';
import { describeFilter, validateFilter } from '../../services/triggerFilterEvaluator';

interface TriggerFilterBuilderProps {
  filters: TriggerFilter[];
  onChange: (filters: TriggerFilter[]) => void;
  availableFields: FilterFieldDefinition[];
  triggerType: string;
}

const TriggerFilterBuilder: React.FC<TriggerFilterBuilderProps> = ({
  filters,
  onChange,
  availableFields,
  triggerType
}) => {
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null);

  // Add new filter
  const handleAddFilter = (field: FilterFieldDefinition) => {
    const newFilter: TriggerFilter = {
      id: `filter_${Date.now()}`,
      field: field.path,
      fieldLabel: field.label,
      fieldType: field.type,
      operator: field.supportedOperators[0], // Default to first supported operator
      value: '',
      logicOperator: filters.length > 0 ? 'AND' : undefined
    };
    
    onChange([...filters, newFilter]);
    setShowFieldSelector(false);
    setSearchTerm('');
    setEditingFilterId(newFilter.id);
  };

  // Update filter
  const handleUpdateFilter = (filterId: string, updates: Partial<TriggerFilter>) => {
    const updatedFilters = filters.map(f =>
      f.id === filterId ? { ...f, ...updates } : f
    );
    onChange(updatedFilters);
  };

  // Remove filter
  const handleRemoveFilter = (filterId: string) => {
    const updatedFilters = filters.filter(f => f.id !== filterId);
    onChange(updatedFilters);
    if (editingFilterId === filterId) {
      setEditingFilterId(null);
    }
  };

  // Toggle logic operator (AND/OR)
  const handleToggleLogic = (filterId: string) => {
    const filter = filters.find(f => f.id === filterId);
    if (filter) {
      const newLogic: FilterLogic = filter.logicOperator === 'AND' ? 'OR' : 'AND';
      handleUpdateFilter(filterId, { logicOperator: newLogic });
    }
  };

  // Group fields by category (derived only where needed)

  // Filter fields by search term
  const filteredFields = searchTerm
    ? availableFields.filter(field =>
        field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : availableFields;

  const filteredFieldsByCategory = filteredFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, FilterFieldDefinition[]>);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Trigger Filters
        </h4>
        {filters.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {filters.length} filter{filters.length !== 1 ? 's' : ''} active
          </span>
        )}
      </div>

      {/* Filter List */}
      {filters.length > 0 && (
        <div className="space-y-2">
          {filters.map((filter, index) => {
            const field = availableFields.find(f => f.path === filter.field);
            const validation = validateFilter(filter);
            const isEditing = editingFilterId === filter.id;

            return (
              <div key={filter.id} className="space-y-2">
                {/* Logic Operator (for filters after the first) */}
                {index > 0 && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => handleToggleLogic(filter.id)}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        filter.logicOperator === 'OR'
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}
                    >
                      {filter.logicOperator || 'AND'}
                    </button>
                  </div>
                )}

                {/* Filter Card */}
                <div
                  className={`border rounded-lg p-4 transition-all ${
                    !validation.valid
                      ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                      : isEditing
                      ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                      : 'border-gray-200 bg-white dark:border-neutral-600 dark:bg-neutral-800'
                  }`}
                >
                  {/* Filter Summary (when not editing) */}
                  {!isEditing && (
                    <div className="flex items-start justify-between group">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {describeFilter(filter)}
                        </p>
                        {!validation.valid && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {validation.errors.join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditingFilterId(filter.id)}
                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Edit filter"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRemoveFilter(filter.id)}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Remove filter"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Filter Editor (when editing) */}
                  {isEditing && field && (
                    <div className="space-y-3">
                      {/* Field Label */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Field
                        </label>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {filter.fieldLabel}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {field.category}
                        </div>
                      </div>

                      {/* Operator Selector */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Condition
                        </label>
                        <select
                          value={filter.operator}
                          onChange={(e) => handleUpdateFilter(filter.id, { 
                            operator: e.target.value as FilterOperator,
                            value: '' // Reset value when operator changes
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          {field.supportedOperators.map(op => (
                            <option key={op} value={op}>
                              {getOperatorLabel(op)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Value Input */}
                      {operatorRequiresValue(filter.operator) && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Value
                          </label>
                          
                          {/* Between (two inputs) */}
                          {operatorRequiresTwoValues(filter.operator) && (
                            <div className="flex items-center space-x-2">
                              <input
                                type={field.type === 'number' ? 'number' : 'text'}
                                value={Array.isArray(filter.value) ? filter.value[0] : ''}
                                onChange={(e) => handleUpdateFilter(filter.id, {
                                  value: [e.target.value, Array.isArray(filter.value) ? filter.value[1] : '']
                                })}
                                placeholder="Min"
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                              />
                              <span className="text-gray-500 dark:text-gray-400 text-sm">and</span>
                              <input
                                type={field.type === 'number' ? 'number' : 'text'}
                                value={Array.isArray(filter.value) ? filter.value[1] : ''}
                                onChange={(e) => handleUpdateFilter(filter.id, {
                                  value: [Array.isArray(filter.value) ? filter.value[0] : '', e.target.value]
                                })}
                                placeholder="Max"
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          )}
                          
                          {/* In/Not In (comma-separated list) */}
                          {operatorRequiresMultipleValues(filter.operator) && (
                            <div>
                              <input
                                type="text"
                                value={Array.isArray(filter.value) ? filter.value.join(', ') : ''}
                                onChange={(e) => handleUpdateFilter(filter.id, {
                                  value: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                                })}
                                placeholder="Enter values separated by commas"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Separate multiple values with commas
                              </p>
                            </div>
                          )}
                          
                          {/* Single value */}
                          {!operatorRequiresTwoValues(filter.operator) && !operatorRequiresMultipleValues(filter.operator) && (
                            <input
                              type={field.type === 'number' ? 'number' : 'text'}
                              value={filter.value}
                              onChange={(e) => handleUpdateFilter(filter.id, { value: e.target.value })}
                              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-neutral-600">
                        <button
                          onClick={() => handleRemoveFilter(filter.id)}
                          className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => setEditingFilterId(null)}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Filter Button */}
      <button
        onClick={() => setShowFieldSelector(!showFieldSelector)}
        className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 dark:border-neutral-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all flex items-center justify-center space-x-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span>Add Trigger Filter</span>
      </button>

      {/* Field Selector Modal */}
      {showFieldSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-neutral-600">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Select Filter Field
                </h3>
                <button
                  onClick={() => setShowFieldSelector(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Search */}
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search fields..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Field List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {Object.keys(filteredFieldsByCategory).length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">No fields found matching "{searchTerm}"</p>
                </div>
              ) : (
                Object.entries(filteredFieldsByCategory).map(([category, fields]) => (
                  <div key={category}>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      {category}
                    </h4>
                    <div className="space-y-1">
                      {fields.map(field => (
                        <button
                          key={field.path}
                          onClick={() => handleAddFilter(field)}
                          className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {field.label}
                              </p>
                              {field.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {field.description}
                                </p>
                              )}
                            </div>
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TriggerFilterBuilder;

